import Vue from "vue";

export const SessionState = {
  session: null,
  sessionDrafts: [],
  sessions: [],
  measurements: [],
  sessionComments: []
};

export const SessionGetters = {
  session: state => {
    return state.session;
  },
  sessionDrafts: state => {
    return state.sessionDrafts;
  },
  sessions: state => {
    return state.sessions;
  },
  measurements: state => {
    return state.measurements;
  },
  sessionComments: state => {
    return state.sessionComments;
  }
};

export const SessionMutations = {
  setSession: (state, data) => {
    if (data) {
      const index = state.sessions.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.sessions];
        arr[index] = data;
        Vue.set(state, "sessions", arr);
      }
      //
      const indexDraft = state.sessionDrafts.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (indexDraft > -1) {
        const arr = [...state.sessionDrafts];
        arr[indexDraft] = data;
        Vue.set(state, "sessionDrafts", arr);
      }
    }
    Vue.set(state, "session", data);
  },
  setSessions: (state, data) => {
    Vue.set(state, "sessions", data);
  },
  setSessionDrafts: (state, data) => {
    Vue.set(state, "sessionDrafts", data);
  },
  addSessionDraft: (state, data) => {
    Vue.set(state, "sessionDrafts", [data, ...state.sessionDrafts]);
  },
  removeSessionDraft: (state, data) => {
    const arr = state.sessionDrafts.filter(
      i => Number(i.id) !== Number(data.id)
    );
    Vue.set(state, "sessionDrafts", arr);
  },
  setSessionAppointment: (state, data) => {
    const session = state.session;
    Vue.set(state, "session", {
      ...session,
      appointment: data,
      appointment_id: data.id
    });
  },
  removeSessionAppointment: state => {
    const session = state.session;
    Vue.set(state, "session", {
      ...session,
      appointment: null,
      appointment_id: null
    });
  },
  //
  setMeasurement: (state, data) => {
    if (data) {
      const index = state.measurements.findIndex(
        i => Number(i.target_id) === Number(data.target_id)
      );
      if (index > -1) {
        const arr = [...state.measurements];
        arr[index] = data;
        Vue.set(state, "measurements", arr);
      }
    }
  },
  setMeasurements: (state, data) => {
    Vue.set(state, "measurements", data);
  },
  addMeasurement: (state, data) => {
    Vue.set(state, "measurements", [...state.measurements, data]);
  },
  removeMeasurement: (state, data) => {
    const arr = state.measurements.filter(
      i => Number(i.id) !== Number(data.id)
    );
    Vue.set(state, "measurements", arr);
  },
  //
  setSessionComment: (state, data) => {
    if (data) {
      if (data.measurement_id) {
        const index = state.sessionComments.findIndex(
          i => Number(i.measurement_id) === Number(data.measurement_id)
        );
        if (index > -1) {
          const arr = [...state.sessionComments];
          arr[index] = data;
          Vue.set(state, "sessionComments", arr);
        }
      } else {
        const index = state.sessionComments.findIndex(
          i => Number(i.id) === Number(data.id)
        );
        if (index > -1) {
          const arr = [...state.sessionComments];
          arr[index] = data;
          Vue.set(state, "sessionComments", arr);
        }
      }
    }
  },
  setSessionComments: (state, data) => {
    Vue.set(state, "sessionComments", data || []);
  },
  addSessionComment: (state, data) => {
    Vue.set(state, "sessionComments", [...state.sessionComments, data]);
  },
  removeSessionComment: (state, data) => {
    const arr = state.sessionComments.filter(
      i => Number(i.id) !== Number(data.id)
    );
    Vue.set(state, "sessionComments", arr);
  }
};

export const SessionActions = {
  getSessions: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/clients/${data.id}/sessions${params}`)
      .then(response => {
        if (params.includes("completed") || params.includes("cancelled")) {
          // completed,cancelled
          commit("setSessions", response.data.sessions);
        } else if (params.includes("draft") || params.includes("ongoing")) {
          // draft,ongoing
          commit("setSessionDrafts", response.data.sessions);
        } else {
          // no filtered
          commit("setSessions", response.data.sessions);
        }
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch sessions from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getSession: ({ commit, dispatch }, data = {}) => {
    commit("setSession", null);
    commit("setSessionComments", []);
    return Vue.axios.secured
      .get(`/api/v1/sessions/${data.slug}`)
      .then(response => {
        commit("setSession", response.data);
        commit("setSessionComments", response.data.session_comments);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch session from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/clients/${data.client_id}/sessions`, {
        session: data.session
      })
      .then(response => {
        commit("addSessionDraft", response.data);
        const message = `Session has been successfully added.`;
        dispatch("showToast", { message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/sessions/${data.id}`, { session: data.session })
      .then(response => {
        commit("setSession", response.data);
        let message = "";
        if (data.session.status === "ongoing") {
          message = `Session record has started`;
        }
        if (message) dispatch("showToast", { message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/sessions/${data.id}`)
      .then(response => {
        commit("removeSessionDraft", data);
        const message = `Session has been successfully removed.`;
        dispatch("showToast", { message, type: "success" });
        return { success: true };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false };
      });
  },
  downloadSessionTemplate: ({ dispatch }, data = {}) => {
    const detailed = data.detailed ? "with_comment=true" : "";
    return Vue.axios.secured
      .get(`/api/v1/sessions/${data.id}/download_template?${detailed}`, {
        responseType: "blob"
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to download session template.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createScheduleSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/sessions/${data.id}/schedule`, {
        appointment: data.appointment
      })
      .then(response => {
        commit("setSessionAppointment", response.data);
        const title = `Session scheduled`;
        const message = `Success! This session has been scheduled.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        console.error(error.response);
        return {
          success: false,
          data: null,
          conflicted_dates: error.response.data.error.conflicted_dates || []
        };
      });
  },
  updateScheduleSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/appointments/${data.appointment_id}`, {
        appointment: data.appointment
      })
      .then(response => {
        commit("setSessionAppointment", response.data);
        const title = `Session schedule updated`;
        const message = `Success! This session schedule has been updated.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        console.error(error.response);
        return {
          success: false,
          data: null,
          conflicted_dates: error.response.data.error.conflicted_dates || []
        };
      });
  },
  deleteScheduleSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/sessions/${data.id}/unschedule`)
      .then(response => {
        commit("removeSessionAppointment");
        const title = `Session unscheduled`;
        const message = `Success! This session has been unscheduled.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to unschedule session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  duplicateTargetsSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/sessions/${data.id}/duplicate_targets`, {
        selected_session_id: data.selected_session_id
      })
      .then(response => {
        commit("setSession", response.data);
        dispatch("getMeasurements", { id: data.id });
        const title = `Session duplicated`;
        const message = `Success! This session has been duplicated from session ID ${data.selected_session_id}.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to duplicate targets session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  addMultipleTargetsSession: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/sessions/${data.id}/add_multiple_targets`, {
        target_ids: data.target_ids
      })
      .then(response => {
        commit("setSession", response.data);
        dispatch("getMeasurements", { id: data.id });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to add multiple targets session.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getMeasurements: ({ commit, dispatch }, data = {}) => {
    const skipAction = data.skip_action || false;
    return Vue.axios.secured
      .get(`/api/v1/sessions/${data.id}/measurements${data.params || ""}`)
      .then(response => {
        if (skipAction) {
          return { success: true, data: response.data };
        }
        commit("setMeasurements", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch measurements from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: [] };
      });
  },
  getMeasurement: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/measurements/${data.id}`)
      .then(response => {
        commit("setMeasurement", response.data);
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch measurement from server.";
        dispatch("showToast", { message, type: "error" });
      });
  },
  createMeasurement: ({ commit, dispatch }, data = {}) => {
    const skipAction = data.skip_action || false;
    return Vue.axios.secured
      .post(
        `/api/v1/sessions/${data.id}/targets/${data.target_id}/measurements`,
        { measurement: data.measurement }
      )
      .then(response => {
        if (skipAction) {
          return { success: true, data: response.data };
        }
        commit("addMeasurement", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateMeasurement: ({ commit, dispatch }, data = {}) => {
    const skipAction = data.skip_action || false;
    return Vue.axios.secured
      .patch(`/api/v1/measurements/${data.id}`, {
        measurement: data.measurement
      })
      .then(response => {
        if (skipAction) {
          return { success: true, data: response.data };
        }
        commit("setMeasurement", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateMeasurementIsDropped: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/measurements/${data.id}`, {
        measurement: data.measurement
      })
      .then(response => {
        commit("setMeasurement", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateMeasurementResults: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/measurements/${data.id}/update_results`, {
        results: data.results
      })
      .then(response => {
        commit("setMeasurement", response.data);
        return { success: true, data: response.data, message: "" };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null, message };
      });
  },
  updateMeasurementMarkProbing: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/measurements/${data.id}/mark_probing`, data)
      .then(response => {
        commit("setMeasurement", response.data);
        return { success: true, data: response.data, message: "" };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null, message };
      });
  },
  deleteMeasurement: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    const skipAction = data.skip_action || false;
    return Vue.axios.secured
      .delete(`/api/v1/measurements/${data.id}${params}`)
      .then(response => {
        if (skipAction) {
          return { success: true, data: response.data };
        }
        commit("removeMeasurement", data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete measurement.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getSessionComments: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/sessions/${data.id}/comments${params}`)
      .then(response => {
        commit("setSessionComments", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch session comments from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getSessionComment: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/session_comments/${data.id}`)
      .then(response => {
        commit("setSessionComment", response.data);
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch session comment from server.";
        dispatch("showToast", { message, type: "error" });
      });
  },
  createSessionComment: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/sessions/${data.id}/session_comments`, {
        session_comment: data.session_comment
      })
      .then(response => {
        commit("addSessionComment", response.data);
        const title = `New comment added`;
        const message = `Success! The comment has been added.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create session comment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateSessionComment: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/session_comments/${data.id}`, {
        session_comment: data.session_comment
      })
      .then(response => {
        commit("setSessionComment", response.data);
        const title = `Comment updated`;
        const message = `Success! The comment has been updated.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update session comment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteSessionComment: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/session_comments/${data.id}`)
      .then(response => {
        commit("removeSessionComment", data);
        const title = `Comment deleted`;
        const message = `Success! The comment has been deleted.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete session comment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/session.store.js